import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { siteInfo, testimonials, faqs } from '@/mocks/comics';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于爱漫画 - 韩国漫画在线阅读平台',
  description: siteInfo.description,
  url: 'https://aimanga.com/about',
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '爱漫画',
  url: 'https://aimanga.com/',
  description: siteInfo.description,
  email: siteInfo.email,
  telephone: siteInfo.phone,
  address: {
    '@type': 'PostalAddress',
    addressLocality: '北京',
    addressRegion: '朝阳区',
    streetAddress: '望京SOHO T3 18层',
  },
  foundingDate: '2020',
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="关于我们"
        description={siteInfo.description}
        keywords="爱漫画,关于爱漫画,韩漫平台,韩国漫画在线阅读,免费韩漫大全"
        canonicalUrl="https://aimanga.com/about"
        schema={{ ...orgSchema, ...aboutSchema }}
      />
      <Navbar />
      <main>
        <section className="w-full pt-20 pb-12 md:pb-16 bg-background-50">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />
          <div className="px-4 md:px-6 max-w-7xl mx-auto mt-8">
            <div className="max-w-3xl">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">
                品牌介绍
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-foreground-950 mt-4 leading-tight">
                关于<em className="not-italic text-primary-500">爱漫画</em>
              </h1>
              <p className="mt-6 text-lg text-foreground-600 leading-relaxed">
                {siteInfo.description}
              </p>
              <p className="mt-4 text-foreground-600 leading-relaxed">
                自{siteInfo.founded}成立以来，爱漫画始终秉持"让每一位读者都能畅享优质韩漫"的理念，汇聚了2000+部优质韩国漫画作品，涵盖恋爱、奇幻、热血、悬疑、搞笑等多种类型。我们坚持提供无删减、高品质的阅读内容，并持续优化下拉式阅读体验，让您在任何设备上都能享受流畅的漫画阅读乐趣。
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#contact" className="whitespace-nowrap px-8 py-3 rounded-full bg-primary-500 text-background-50 text-base font-semibold hover:bg-primary-600 transition-all cursor-pointer">
                联系我们
              </a>
              <a href="#join" className="whitespace-nowrap px-8 py-3 rounded-full border-2 border-foreground-200 text-foreground-700 text-base font-medium hover:border-primary-300 hover:text-primary-600 transition-all cursor-pointer">
                加入我们
              </a>
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="mb-10">
              <p className="text-sm text-foreground-500 font-medium tracking-wide">核心优势</p>
              <h2 className="font-heading text-3xl md:text-5xl font-black text-foreground-950 mt-2">
                为什么选择爱漫画
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {[
                { icon: 'ri-book-open-line', title: '海量资源', desc: '2000+部优质韩国漫画，涵盖八大热门分类，每日持续上架新作品。' },
                { icon: 'ri-download-line', title: '无删减版', desc: '坚持提供完整无删减内容，让您享受原汁原味的韩漫阅读体验。' },
                { icon: 'ri-smartphone-line', title: '下拉式阅读', desc: '专为网页端优化的下拉式阅读模式，流畅丝滑，无需频繁翻页。' },
                { icon: 'ri-speed-up-line', title: '更新快速', desc: '编辑团队全天候追踪最新韩漫动态，热门作品24小时内同步上线。' },
                { icon: 'ri-shield-check-line', title: '安全稳定', desc: '采用先进技术架构，保障阅读流畅不卡顿，用户数据安全可靠。' },
                { icon: 'ri-heart-2-line', title: '读者至上', desc: '以读者体验为核心，持续优化产品功能，打造最好的韩漫阅读平台。' },
              ].map((feature, i) => (
                <div
                  key={i}
                  className="flex flex-col gap-3 p-6 md:p-8 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${feature.icon} text-lg`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              <div className="flex-1">
                <p className="text-sm text-foreground-500 font-medium tracking-wide">
                  <i className="ri-double-quotes-l text-primary-500 mr-1"></i>
                  读者口碑
                </p>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-foreground-950 mt-2 leading-tight">
                  听听读者怎么说
                </h2>

                <div className="mt-8 space-y-6">
                  {testimonials.map((t, i) => (
                    <div key={i} className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70">
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: t.rating }).map((_, j) => (
                          <i key={j} className="ri-star-fill text-accent-400 text-sm"></i>
                        ))}
                      </div>
                      <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
                        &ldquo;{t.content}&rdquo;
                      </p>
                      <div className="mt-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="font-heading text-primary-600 font-bold text-sm">
                            {t.name[0]}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                          <p className="text-xs text-foreground-500">{t.role}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="hidden lg:block w-80 lg:w-96 shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden sticky top-24">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20and%20artistic%20black%20and%20white%20portrait%20photograph%20of%20a%20young%20Asian%20person%20reading%20a%20comic%20book%20with%20a%20gentle%20smile%2C%20soft%20natural%20window%20light%2C%20minimalist%20composition%2C%20clean%20white%20background%2C%20documentary%20style%20portrait%20photography%2C%20cinematic%20mood%2C%20high%20contrast%20black%20and%20white&width=720&height=960&seq=about-testimonial-portrait&orientation=portrait"
                    alt="爱漫画读者"
                    className="w-full h-full object-cover rounded-lg"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="w-full py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="font-heading text-3xl md:text-4xl font-black text-foreground-950">
                联系我们
              </h2>
              <p className="mt-4 text-foreground-600 leading-relaxed">
                有任何问题或建议？欢迎随时与我们取得联系。
              </p>
            </div>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[
                { icon: 'ri-mail-line', label: '电子邮箱', value: siteInfo.email },
                { icon: 'ri-phone-line', label: '客服电话', value: siteInfo.phone },
                { icon: 'ri-wechat-line', label: '微信公众号', value: siteInfo.wechat },
                { icon: 'ri-map-pin-line', label: '公司地址', value: siteInfo.address },
              ].map((item, i) => (
                <div
                  key={i}
                  className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70 text-center"
                >
                  <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <h4 className="mt-3 font-heading text-sm font-semibold text-foreground-900">
                    {item.label}
                  </h4>
                  <p className="mt-1 text-sm text-foreground-600">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="max-w-2xl mx-auto">
              <h2 className="font-heading text-3xl md:text-4xl font-black text-foreground-950 text-center">
                常见问题
              </h2>

              <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

              <div className="mt-10 space-y-3">
                {faqs.map((faq, i) => (
                  <details key={i} className="group rounded-lg bg-background-100 border border-background-200/70 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 md:px-6 py-4 cursor-pointer hover:bg-background-200/50 transition-colors">
                      <span className="font-heading text-sm md:text-base font-semibold text-foreground-900 pr-4">
                        {faq.question}
                      </span>
                      <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform shrink-0"></i>
                    </summary>
                    <div className="px-5 md:px-6 pb-5">
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}